import React, { useState, useEffect, useRef } from 'react';
import { useSimulationStore } from '../stores/useSimulationStore';
import { useSessionsSSE }     from '../stores/useSessionsSSE';
import { useSimSessionFocus } from '../stores/useSimSessionFocus';
import { SessionCard }        from '../components/SessionCard';
import { DesktopWindowManager } from '../components/DesktopWindowManager';
import { SovereignSplash }    from '../../../components/security/SovereignSplash';
import { sairDaSimulacao, markSimCockpit } from '../../sim-onboarding';
import { useAuthStore } from '../../../store/authStore';

//#region AC_BLOCK simulation-dashboard-page -- Dashboard principal com painel SSE flutuante
// @ac-purpose Mantém SovereignSplash intacto + painel flutuante draggable de sessões multi-cidade
// @ac-related react-app/src/features/developer-cockpit/stores/useSessionsSSE.ts#use-sessions-sse
// @ac-related react-app/src/features/developer-cockpit/components/SessionCard.tsx#session-card

const PANEL_W = 340;
const PANEL_INITIAL = { x: window.innerWidth - PANEL_W - 20, y: 80 };

function isAdminUser(): boolean {
  try {
    const u = useAuthStore.getState().user
      || JSON.parse(localStorage.getItem('ac_user') || 'null');
    const p = String(u?.perfil || u?.perfilTipo || '').toUpperCase();
    return p === 'ADMIN' || p === 'ADMINISTRADOR' || p === 'MASTER';
  } catch {
    return false;
  }
}

export function SimulationDashboardPage() {
  const { stopSimulation } = useSimulationStore();
  const authUser = useAuthStore((s) => s.user);
  const showSessionsPanel = isAdminUser() || String(authUser?.perfil || '').toUpperCase() === 'ADMIN';

  // Garante restauração do Prefeito/cockpit no próximo refresh
  useEffect(() => {
    markSimCockpit();
  }, []);

  const [isBackendOnline, setIsBackendOnline] = useState(true);
  const [panelOpen, setPanelOpen]             = useState(true);
  const [filtroStatus, setFiltroStatus]       = useState<string>('active');

  // Posição do painel (draggable)
  const [pos, setPos]   = useState(PANEL_INITIAL);
  const dragRef         = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);

  // SSE sempre ativo — telemetria (Prefeito) e painel (Admin) compartilham o mesmo foco
  const {
    sessions, connected, lastUpdate, error,
    iniciar, pausar, retomar, encerrar, refresh,
  } = useSessionsSSE(true);

  const focusedSessionId = useSimSessionFocus((s) => s.focusedSessionId);
  const setFocusedSession = useSimSessionFocus((s) => s.setFocusedSession);
  const syncFromSessions = useSimSessionFocus((s) => s.syncFromSessions);

  useEffect(() => {
    syncFromSessions(sessions);
  }, [sessions, syncFromSessions]);

  // Quando o filtro muda, busca a lista correta no backend (ativas vs todas vs encerradas)
  useEffect(() => {
    if (!showSessionsPanel) return;
    const mode = filtroStatus === 'active' ? 'active' : filtroStatus === 'STOPPED' ? 'STOPPED' : 'all';
    refresh(mode);
  }, [filtroStatus, refresh, showSessionsPanel]);

  // Health check
  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    const check = async () => {
      try {
        const r = await fetch('/api/v1/dev/health');
        setIsBackendOnline(r.ok && (await r.json()).status === 'ok');
      } catch { setIsBackendOnline(false); }
    };
    timer = setInterval(check, isBackendOnline ? 5000 : 10000);
    return () => clearInterval(timer);
  }, [isBackendOnline]);

  // Drag handlers
  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };
    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      const nx = dragRef.current.origX + (ev.clientX - dragRef.current.startX);
      const ny = dragRef.current.origY + (ev.clientY - dragRef.current.startY);
      setPos({ x: Math.max(0, Math.min(window.innerWidth - PANEL_W, nx)), y: Math.max(0, Math.min(window.innerHeight - 60, ny)) });
    };
    const onUp = () => { dragRef.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Sessões filtradas
  const sessionsFiltradas = filtroStatus === 'active'
    ? sessions.filter(s => s.status === 'RUNNING' || s.status === 'PAUSED' || s.status === 'PREPARING')
    : filtroStatus === 'all'
    ? sessions
    : sessions.filter(s => s.status === filtroStatus);

  const ativas   = sessions.filter(s => s.status === 'RUNNING').length;
  const pausadas = sessions.filter(s => s.status === 'PAUSED').length;

  return (
    <div className="w-screen h-screen overflow-hidden text-[#cdd6f4] select-none relative">
      {/* ── Tela principal — SovereignSplash intacto ── */}
      <SovereignSplash
        skipIntro
        alignLogoRight={true}
        splitPct={30}
        titleWord1="Aluno"
        titleWord2="Conectado"
        subtitle="Central de Simulação em Tempo Real"
        hideBackButton
      >
        <DesktopWindowManager
          isBackendOnline={isBackendOnline}
          stopSimulation={stopSimulation}
        />
      </SovereignSplash>

      {/* Painel de sessões — exclusivo do ambiente Administrador */}
      {showSessionsPanel && (
      <div
        style={{
          position: 'fixed',
          left: pos.x,
          top:  pos.y,
          width: PANEL_W,
          zIndex: 99999,
          borderRadius: 16,
          background: 'rgba(10,8,34,0.92)',
          border: '1px solid rgba(124,58,237,0.35)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(124,58,237,0.1)',
          backdropFilter: 'blur(20px)',
          fontFamily: "'Inter','Segoe UI',system-ui,sans-serif",
          overflow: 'hidden',
          transition: 'box-shadow 0.2s ease',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: panelOpen ? '80vh' : 46,
        }}
      >
        {/* Header do painel — área de drag */}
        <div
          onMouseDown={onMouseDown}
          style={{
            padding: '10px 12px',
            display: 'flex', alignItems: 'center', gap: 8,
            borderBottom: panelOpen ? '1px solid rgba(255,255,255,0.06)' : 'none',
            cursor: 'grab',
            flexShrink: 0,
            userSelect: 'none',
            background: 'rgba(124,58,237,0.08)',
          }}
        >
          {/* Dot SSE */}
          <div style={{
            width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
            background: connected ? '#4ade80' : '#f87171',
            boxShadow: connected ? '0 0 6px #4ade80' : 'none',
          }} />

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#f1f5f9', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              Sessões de Simulação
            </div>
            <div style={{ fontSize: 9, color: '#64748b', marginTop: 1 }}>
              {connected ? `SSE ativo · ${lastUpdate?.toLocaleTimeString('pt-BR') ?? '—'}` : 'Reconectando…'}
              {error ? ` · ${error}` : ''}
              {ativas > 0 && ` · ${ativas} ativa${ativas !== 1 ? 's' : ''}`}
              {pausadas > 0 && ` · ${pausadas} pausada${pausadas !== 1 ? 's' : ''}`}
            </div>
          </div>

          {/* Botões header */}
          <button
            onClick={async (e) => {
              e.stopPropagation();
              if (window.confirm('Deseja sair da simulação e limpar senha, tokens e cookies desta sessão?')) {
                await sairDaSimulacao('/simulador');
              }
            }}
            style={{ width:22, height:22, borderRadius:5, border:'none', background:'rgba(239,68,68,0.15)', color:'#f87171', fontSize:11, cursor:'pointer', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}
            title="Sair da simulação (limpa cache/cookies)"
          >🔒</button>
          <button
            onClick={(e) => { e.stopPropagation(); refresh(filtroStatus === 'active' ? 'active' : filtroStatus === 'STOPPED' ? 'STOPPED' : 'all'); }}
            style={{ width:22, height:22, borderRadius:5, border:'none', background:'rgba(255,255,255,0.05)', color:'#64748b', fontSize:11, cursor:'pointer', flexShrink:0 }}
            title="Atualizar"
          >↻</button>
          <button
            onClick={(e) => { e.stopPropagation(); void sairDaSimulacao('/simulador'); }}
            style={{ width:22, height:22, borderRadius:5, border:'none', background:'rgba(124,58,237,0.2)', color:'#a78bfa', fontSize:12, cursor:'pointer', flexShrink:0 }}
            title="Nova simulação (sair e recomeçar)"
          >+</button>
          <button
            onClick={(e) => { e.stopPropagation(); setPanelOpen(p => !p); }}
            style={{ width:22, height:22, borderRadius:5, border:'none', background:'rgba(255,255,255,0.05)', color:'#64748b', fontSize:11, cursor:'pointer', flexShrink:0 }}
            title={panelOpen ? 'Minimizar' : 'Expandir'}
          >{panelOpen ? '▲' : '▼'}</button>
        </div>

        {/* Corpo do painel */}
        {panelOpen && (
          <>
            {/* Filtros */}
            <div style={{ padding: '8px 10px 6px', display:'flex', gap:4, flexShrink:0, borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
              {([
                { key: 'active', label: 'Ativas' },
                { key: 'all',    label: 'Todas'  },
                { key: 'STOPPED',label: 'Enc.'   },
              ] as const).map(f => (
                <button
                  key={f.key}
                  onClick={() => setFiltroStatus(f.key)}
                  style={{
                    padding: '3px 9px', borderRadius: 6, border: 'none', cursor: 'pointer',
                    fontSize: 10, fontWeight: 700,
                    background: filtroStatus === f.key ? 'rgba(124,58,237,0.25)' : 'rgba(255,255,255,0.04)',
                    color: filtroStatus === f.key ? '#a78bfa' : '#475569',
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Lista de sessões */}
            <div style={{ overflowY: 'auto', padding: '8px 8px', display:'flex', flexDirection:'column', gap:6, flex:1 }}>
              {sessionsFiltradas.length === 0 ? (
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:8, padding:'24px 0', color:'#334155' }}>
                  <span style={{ fontSize:24, opacity:.35 }}>📡</span>
                  <span style={{ fontSize:11 }}>
                    {filtroStatus === 'active' ? 'Nenhuma sessão em andamento.' : 'Nenhuma sessão encontrada.'}
                  </span>
                  <button
                    onClick={() => { void sairDaSimulacao('/simulador'); }}
                    style={{ marginTop:4, padding:'6px 14px', borderRadius:8, border:'none', cursor:'pointer', background:'rgba(124,58,237,0.15)', color:'#a78bfa', fontSize:11, fontWeight:700 }}
                  >
                    + Criar Simulação
                  </button>
                </div>
              ) : (
                sessionsFiltradas.map(s => (
                  <SessionCard
                    key={s.id}
                    session={s}
                    selected={s.id === focusedSessionId}
                    onSelect={setFocusedSession}
                    onIniciar={iniciar}
                    onPausar={pausar}
                    onRetomar={retomar}
                    onEncerrar={encerrar}
                  />
                ))
              )}
            </div>
          </>
        )}
      </div>
      )}

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.5} }
      `}</style>
    </div>
  );
}
//#endregion AC_BLOCK simulation-dashboard-page
